import * as d3 from "d3";

export default class TopologyEditor {
  static GHOST_ID = "__add_junction_ghost__";
  static LEAF_ONLY_TYPES = new Set(["solar_array", "solar_inverter", "charge_point", "connector", "battery"]);

  constructor(controller) {
    this.controller = controller;
    this.enabled = false;
    this.pendingChanges = [];
    this.propertyChanges = new Map();
    this.draggedNode = null;
    this.dropTarget = null;
    this.originalNodes = null;
    this.selectedNodeId = null;
  }

  toggle() {
    if (this.enabled) {
      if (this.hasPendingChanges) {
        this.showConfirmation();
      } else {
        this.cancel();
      }
    } else {
      this.enter();
    }
  }

  enter() {
    this.enabled = true;
    this.pendingChanges = [];
    this.propertyChanges = new Map();
    this.originalNodes = JSON.parse(JSON.stringify(this.controller.nodes));

    this._beforeUnloadHandler = (e) => {
      if (this.hasPendingChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    this._turboBeforeVisitHandler = (e) => {
      if (
        this.hasPendingChanges &&
        !confirm(
          this.controller.i18nUnsavedChangesValue || "You have unsaved changes. Are you sure you want to navigate?",
        )
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", this._beforeUnloadHandler);
    document.addEventListener("turbo:before-visit", this._turboBeforeVisitHandler);

    this.controller.particleEngine.stop();
    this.controller.particleEngine.clearCanvas();
    this.controller.unsubscribeFromChannel();
    this.controller.stopPolling();

    if (this.controller.hasTimeControlsTarget) {
      this.controller.timeControlsTarget.classList.add("hidden");
    }

    this.updateToolbarState();
    this.controller.renderTree();
    this.attachDragBehavior();
  }

  cancel() {
    if (this.originalNodes) {
      this.controller.nodes = this.originalNodes;
    }
    this.exit();
    this.controller.renderTree();
    this.controller.fitToView();
  }

  exit() {
    this.enabled = false;
    this.pendingChanges = [];
    this.propertyChanges = new Map();
    this.originalNodes = null;
    this.draggedNode = null;
    this.dropTarget = null;
    this.selectedNodeId = null;
    this.removeGhostNode();
    this.hideNodeProperties();

    this.removeNavigationProtection();

    if (this.controller.hasTimeControlsTarget) {
      this.controller.timeControlsTarget.classList.remove("hidden");
    }

    this.updateToolbarState();
    this.controller.particleEngine.start();

    if (this.controller.isTodayValue) {
      this.controller.subscribeToChannel();
    }
  }

  removeNavigationProtection() {
    if (this._beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this._beforeUnloadHandler);
      this._beforeUnloadHandler = null;
    }
    if (this._turboBeforeVisitHandler) {
      document.removeEventListener("turbo:before-visit", this._turboBeforeVisitHandler);
      this._turboBeforeVisitHandler = null;
    }
  }

  updateToolbarState() {
    if (!this.controller.hasEditToolbarTarget) return;

    if (this.enabled) {
      this.controller.editToolbarTarget.classList.remove("hidden");
    } else {
      this.controller.editToolbarTarget.classList.add("hidden");
    }

    if (this.controller.hasEditButtonTarget) {
      const btn = this.controller.editButtonTarget;
      if (this.enabled) {
        btn.classList.add("bg-amber-100", "dark:bg-amber-900/30", "text-amber-700", "dark:text-amber-300");
        btn.classList.remove("text-gray-600", "dark:text-gray-300");
      } else {
        btn.classList.remove("bg-amber-100", "dark:bg-amber-900/30", "text-amber-700", "dark:text-amber-300");
        btn.classList.add("text-gray-600", "dark:text-gray-300");
      }
    }
  }

  attachDragBehavior() {
    if (!this.enabled) return;

    const self = this;
    const nodeGroups = this.controller.mainGroup.selectAll(".node-box");

    const drag = d3
      .drag()
      .on("start", function (event, d) {
        if (d.data.type === "grid_connection") return;
        if (d.data.type === "solar_array") return;
        if (d.data.type === "add_junction_ghost") return;

        self.draggedNode = d;
        self.dragMoved = false;
        self.dragDistance = 0;
        self.dragElement = d3.select(this);
        self.controller.treeRenderer.hideTooltip();
      })
      .on("drag", function (event, d) {
        if (!self.draggedNode) return;

        self.dragDistance += Math.abs(event.dx) + Math.abs(event.dy);
        const DRAG_THRESHOLD = 8;
        if (self.dragDistance < DRAG_THRESHOLD) return;

        if (!self.dragMoved) {
          self.dragMoved = true;
          self.dragElement.raise().classed("dragging", true);
          self.dragElement.select("rect").attr("opacity", 0.7).attr("stroke-dasharray", "5,3");
          self.highlightDropTargets(d);
        }

        self.controller.treeRenderer.hideTooltip();

        const transform = self.controller.currentTransform;
        const dx = event.dx / (transform?.k || 1);
        const dy = event.dy / (transform?.k || 1);

        const current = d3.select(this);
        const currentTransform = current.attr("transform");
        const match = currentTransform.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/);
        if (match) {
          const newX = parseFloat(match[1]) + dx;
          const newY = parseFloat(match[2]) + dy;
          current.attr("transform", `translate(${newX}, ${newY})`);
        }

        self.updateDropTarget(event, d);
      })
      .on("end", function (event, d) {
        if (!self.draggedNode) return;

        d3.select(this).classed("dragging", false);
        d3.select(this).select("rect").attr("opacity", 1).attr("stroke-dasharray", null);
        self.clearDropTargetHighlights();

        if (!self.dragMoved) {
          self.draggedNode = null;
          self.selectNode(d.data.id);
          return;
        }

        const droppedNodeId = d.data.id;

        if (self.dropTarget && self.dropTarget.data.id !== d.data.parent) {
          self.reparentNode(droppedNodeId, self.dropTarget.data.id);
        }

        self.draggedNode = null;
        self.dropTarget = null;
        self.selectedNodeId = null;

        self.selectNode(droppedNodeId);
      });

    nodeGroups.call(drag);

    const nonDraggableTypes = new Set(["grid_connection", "solar_array"]);
    nodeGroups
      .filter((d) => nonDraggableTypes.has(d.data.type))
      .on("click", function (event, d) {
        event.stopPropagation();
        self.selectNode(d.data.id);
      });
  }

  highlightDropTargets(draggedNode) {
    const descendants = new Set();
    this.collectDescendants(draggedNode.data.id, descendants);

    this.controller.mainGroup.selectAll(".node-box").each(function (d) {
      if (d.data.id === draggedNode.data.id) return;
      if (d.data.type === "add_junction_ghost") return;
      if (descendants.has(d.data.id)) return;
      if (TopologyEditor.LEAF_ONLY_TYPES.has(d.data.type)) return;

      d3.select(this).classed("drop-target", true);
      d3.select(this).select("rect").attr("stroke-width", 3).attr("stroke", "var(--color-action-fill, #3b82f6)");
    });
  }

  collectDescendants(nodeId, descendants) {
    this.controller.nodes.forEach((n) => {
      if (n.parent === nodeId) {
        descendants.add(n.id);
        this.collectDescendants(n.id, descendants);
      }
    });
  }

  updateDropTarget(event, draggedNode) {
    const transform = this.controller.currentTransform;
    if (!transform) return;

    const svgPoint = this.controller.svgTarget.createSVGPoint();
    svgPoint.x = event.sourceEvent.clientX;
    svgPoint.y = event.sourceEvent.clientY;
    const ctm = this.controller.mainGroup.node().getScreenCTM().inverse();
    const transformed = svgPoint.matrixTransform(ctm);

    const descendants = new Set();
    this.collectDescendants(draggedNode.data.id, descendants);

    let closestNode = null;
    let closestDist = Infinity;

    this.controller.mainGroup.selectAll(".node-box.drop-target").each(function (d) {
      const nodeX = d.x;
      const nodeY = d.y;
      const dist = Math.sqrt(Math.pow(transformed.x - nodeX, 2) + Math.pow(transformed.y - nodeY, 2));

      if (dist < closestDist && dist < 100) {
        closestDist = dist;
        closestNode = { element: this, data: d };
      }
    });

    this.controller.mainGroup.selectAll(".node-box.drop-target").each(function () {
      d3.select(this).classed("drop-hover", false);
      d3.select(this).select("rect").attr("stroke-width", 3).attr("stroke", "var(--color-action-fill, #3b82f6)");
    });

    if (closestNode) {
      this.dropTarget = closestNode.data;
      d3.select(closestNode.element).classed("drop-hover", true);
      d3.select(closestNode.element)
        .select("rect")
        .attr("stroke-width", 4)
        .attr("stroke", "var(--color-box-fill-confirm, #22c55e)");
    } else {
      this.dropTarget = null;
    }
  }

  clearDropTargetHighlights() {
    this.controller.mainGroup.selectAll(".node-box").each(function () {
      d3.select(this).classed("drop-target", false).classed("drop-hover", false);
      d3.select(this).select("rect").attr("stroke-width", null).attr("stroke", null);
    });
  }

  selectNode(nodeId) {
    if (nodeId === this.selectedNodeId) {
      this.deselectNode();
      return;
    }

    this.removeGhostNode();
    this.selectedNodeId = nodeId;

    const selectedNode = this.controller.nodes.find((n) => n.id === nodeId);
    const canHaveChildren =
      selectedNode &&
      (!TopologyEditor.LEAF_ONLY_TYPES.has(selectedNode.type) || selectedNode.type === "solar_inverter");
    if (canHaveChildren) {
      this.controller.nodes.push({
        id: TopologyEditor.GHOST_ID,
        name: "+",
        type: "add_junction_ghost",
        parent: nodeId,
      });
    }

    this.controller.renderTree();
    this.attachDragBehavior();
    this.attachGhostClickHandler();
    this.highlightSelectedNode();
    this.attachDeleteButton();
    this.showNodeProperties(nodeId);
  }

  deselectNode() {
    this.selectedNodeId = null;
    this.removeGhostNode();
    this.hideNodeProperties();
    this.controller.renderTree();
    this.attachDragBehavior();
  }

  removeGhostNode() {
    this.controller.nodes = this.controller.nodes.filter((n) => n.id !== TopologyEditor.GHOST_ID);
  }

  attachGhostClickHandler() {
    const self = this;
    this.controller.mainGroup.selectAll(".node-ghost").on("click", function (event) {
      event.stopPropagation();
      const selectedNode = self.controller.nodes.find((n) => n.id === self.selectedNodeId);
      if (selectedNode && selectedNode.type === "solar_inverter") {
        self.removeGhostNode();
        self.selectedNodeId = null;
        self.addSolarArrayToInverter(selectedNode.id);
      } else {
        self.showNodeTypeMenu(d3.select(this));
      }
    });
  }

  showNodeTypeMenu(ghostElement) {
    this.controller.mainGroup.selectAll(".node-type-menu").remove();

    const parentId = this.selectedNodeId;
    const self = this;
    const nodeTypes = this.controller.i18nNodeTypesValue || {};

    const ghost = ghostElement.datum();
    const menuX = ghost.x + this.controller.nodeWidth + 8;
    const menuY = ghost.y - this.controller.nodeHeight / 2;

    const options = [
      { label: this.controller.i18nAddJunctionMenuValue || "Junction", type: "junction" },
      { label: this.controller.i18nAddSolarValue || "Inverter + Solar Array", type: "solar_array" },
      { label: this.controller.i18nAddBatteryValue || "Battery", type: "battery" },
    ];

    const menu = this.controller.mainGroup.append("g").attr("class", "node-type-menu");

    const itemHeight = 28;
    const padding = 4;
    const menuWidth = 180;
    const menuHeight = options.length * itemHeight + padding * 2;

    menu
      .append("rect")
      .attr("x", menuX)
      .attr("y", menuY)
      .attr("width", menuWidth)
      .attr("height", menuHeight)
      .attr("rx", 6)
      .attr("fill", "var(--color-fill-elevation-level-2, #1f2937)")
      .attr("stroke", "var(--color-border-primary, #374151)")
      .attr("stroke-width", 1)
      .style("filter", "drop-shadow(0 4px 6px rgba(0,0,0,0.3))");

    options.forEach((opt, i) => {
      const itemG = menu
        .append("g")
        .style("cursor", "pointer")
        .on("click", function (event) {
          event.stopPropagation();
          menu.remove();
          self.removeGhostNode();
          self.selectedNodeId = null;
          if (opt.type === "junction") {
            self.addJunction(parentId);
          } else if (opt.type === "solar_array") {
            self.addSolarArray(parentId);
          } else if (opt.type === "battery") {
            self.addBattery(parentId);
          }
        });

      itemG
        .append("rect")
        .attr("x", menuX + padding)
        .attr("y", menuY + padding + i * itemHeight)
        .attr("width", menuWidth - padding * 2)
        .attr("height", itemHeight)
        .attr("rx", 4)
        .attr("fill", "transparent")
        .on("mouseenter", function () {
          d3.select(this).attr("fill", "var(--color-action-fill, #3b82f6)").attr("opacity", 0.2);
        })
        .on("mouseleave", function () {
          d3.select(this).attr("fill", "transparent").attr("opacity", 1);
        });

      itemG
        .append("text")
        .attr("x", menuX + padding + 10)
        .attr("y", menuY + padding + i * itemHeight + itemHeight / 2)
        .attr("dominant-baseline", "central")
        .attr("fill", "var(--color-text-primary, #f3f4f6)")
        .attr("font-size", "12px")
        .text(opt.label)
        .style("pointer-events", "none");
    });

    const dismiss = (event) => {
      if (!event.target.closest || !menu.node().contains(event.target)) {
        menu.remove();
        document.removeEventListener("click", dismiss);
      }
    };
    setTimeout(() => document.addEventListener("click", dismiss), 0);
  }

  highlightSelectedNode() {
    const selectedId = this.selectedNodeId;
    this.controller.mainGroup.selectAll(".node-box").each(function (d) {
      if (d.data.id === selectedId) {
        d3.select(this).select("rect").attr("stroke", "var(--color-action-fill, #3b82f6)").attr("stroke-width", 3);
      }
    });
  }

  attachDeleteButton() {
    const selectedId = this.selectedNodeId;
    if (!selectedId) return;

    const node = this.controller.nodes.find((n) => n.id === selectedId);
    const isInverter = node && node.type === "solar_inverter";
    const removable = node && (node.type === "junction" || isInverter || node.type === "battery");
    if (!removable) return;

    const hasChildren =
      !isInverter && this.controller.nodes.some((n) => n.parent === selectedId && n.id !== TopologyEditor.GHOST_ID);
    const self = this;
    const r = 12;

    this.controller.mainGroup.selectAll(".node-box").each(function (d) {
      if (d.data.id !== selectedId) return;

      const group = d3.select(this);
      const btnGroup = group
        .append("g")
        .attr("class", "delete-btn")
        .attr("transform", `translate(${self.controller.nodeWidth + 6}, -6)`)
        .style("cursor", "pointer");

      btnGroup
        .append("circle")
        .attr("r", r)
        .attr("fill", "#ef4444")
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 2)
        .style("filter", "drop-shadow(0 2px 4px rgba(239,68,68,0.5))");

      btnGroup
        .append("text")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("fill", "white")
        .attr("font-size", "16px")
        .attr("font-weight", "bold")
        .style("pointer-events", "none")
        .text("\u00d7");

      btnGroup
        .on("mousedown", function (event) {
          event.stopPropagation();
        })
        .on("click", function (event) {
          event.stopPropagation();
          if (hasChildren) {
            self.showChildrenWarning(d);
            return;
          }
          self.hideNodeProperties();
          self.selectedNodeId = null;
          self.removeGhostNode();
          self.removeNode(d.data.id);
        });
    });
  }

  showChildrenWarning(d) {
    const warning = this.controller.i18nHasChildrenWarningValue || "Remove all child nodes first";
    const existing = this.controller.mainGroup.select(".children-warning");
    if (!existing.empty()) existing.remove();

    const group = this.controller.mainGroup
      .append("g")
      .attr("class", "children-warning")
      .attr(
        "transform",
        `translate(${d.x + this.controller.nodeWidth / 2}, ${d.y - this.controller.nodeHeight / 2 - 12})`,
      );

    const textWidth = warning.length * 5.5 + 16;

    group
      .append("rect")
      .attr("x", -textWidth / 2)
      .attr("y", -12)
      .attr("width", textWidth)
      .attr("height", 24)
      .attr("rx", 6)
      .attr("fill", "#ef4444")
      .attr("opacity", 0.95)
      .style("filter", "drop-shadow(0 2px 4px rgba(0,0,0,0.2))");

    group
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("fill", "white")
      .attr("font-size", "11px")
      .attr("font-weight", "500")
      .text(warning);

    setTimeout(() => group.remove(), 3000);
  }

  showNodeProperties(nodeId) {
    if (!this.controller.hasNodePropertiesTarget) return;

    const node = this.controller.nodes.find((n) => n.id === nodeId);
    if (!node) {
      this.hideNodeProperties();
      return;
    }

    const nodeTypes = this.controller.i18nNodeTypesValue || {};
    const typeLabel = nodeTypes[node.type] || node.type;

    if (this.controller.hasNodeTypeNameTarget) {
      this.controller.nodeTypeNameTarget.textContent = typeLabel;
    }
    if (this.controller.hasNodeTypeIconTarget) {
      const iconPath = this.controller.treeRenderer.getNodeIcon(node.type);
      this.controller.nodeTypeIconTarget.querySelector("path").setAttribute("d", iconPath);
    }
    const isJunction = node.type === "junction";
    const isGridConnection = node.type === "grid_connection";
    const isChargePoint = node.type === "charge_point";
    const isSolarArray = node.type === "solar_array";
    const isSolarInverter = node.type === "solar_inverter";
    const isBattery = node.type === "battery";
    const isReadOnly = isGridConnection || isChargePoint || isSolarArray || isSolarInverter;

    if (this.controller.hasNodeDisplayNameTarget) {
      this.controller.nodeDisplayNameTarget.textContent =
        isJunction || isGridConnection || isSolarInverter ? "" : node.name || "";
    }
    if (this.controller.hasNodeEditLinkTarget) {
      const returnTo = this.controller.hasReturnToUrlValue ? `?return_to=${encodeURIComponent(this.controller.returnToUrlValue)}` : "";
      if (isGridConnection && this.controller.hasSiteEditUrlValue) {
        this.controller.nodeEditLinkTarget.href = `${this.controller.siteEditUrlValue}${returnTo}`;
        this.controller.nodeEditLinkTarget.classList.remove("hidden");
      } else if (isChargePoint && node.nodeable_id && this.controller.hasChargePointEditUrlTemplateValue) {
        this.controller.nodeEditLinkTarget.href = `${this.controller.chargePointEditUrlTemplateValue.replace("__CHARGE_POINT_ID__", node.nodeable_id)}${returnTo}`;
        this.controller.nodeEditLinkTarget.classList.remove("hidden");
      } else if (isSolarArray && node.nodeable_id && this.controller.hasSolarArrayEditUrlTemplateValue) {
        this.controller.nodeEditLinkTarget.href = `${this.controller.solarArrayEditUrlTemplateValue.replace("__SOLAR_ARRAY_ID__", node.nodeable_id)}${returnTo}`;
        this.controller.nodeEditLinkTarget.classList.remove("hidden");
      } else {
        this.controller.nodeEditLinkTarget.classList.add("hidden");
      }
    }
    const showPowerFields = isJunction || isGridConnection || isChargePoint || isBattery;

    if (this.controller.hasFeedInLabelTarget) {
      if (isBattery) {
        this.controller.feedInLabelTarget.textContent = this.controller.i18nMaxDischargeValue || "Max discharge";
      } else {
        this.controller.feedInLabelTarget.textContent = isGridConnection
          ? this.controller.i18nMaxFeedInValue
          : this.controller.i18nMaxProductionValue;
      }
    }
    if (this.controller.hasConsumptionLabelTarget) {
      this.controller.consumptionLabelTarget.textContent = isBattery
        ? this.controller.i18nMaxChargeValue || "Max charge"
        : this.controller.i18nMaxConsumptionValue;
    }

    if (this.controller.hasJunctionFieldsTarget) {
      this.controller.junctionFieldsTarget.classList.toggle("hidden", !showPowerFields);
    }
    if (this.controller.hasNameFieldWrapperTarget) {
      this.controller.nameFieldWrapperTarget.classList.toggle("hidden", isReadOnly);
    }
    if (this.controller.hasSymmetricWrapperTarget) {
      this.controller.symmetricWrapperTarget.classList.toggle("hidden", isChargePoint || isBattery);
    }
    if (this.controller.hasReadOnlyPowerTarget) {
      this.controller.readOnlyPowerTarget.classList.toggle("hidden", !isChargePoint);
    }
    if (this.controller.hasPowerInputsTarget) {
      this.controller.powerInputsTarget.classList.toggle("hidden", isChargePoint);
    }
    if (this.controller.hasBatteryFieldsTarget) {
      this.controller.batteryFieldsTarget.classList.toggle("hidden", !isBattery);
    }

    if (isChargePoint && this.controller.hasReadOnlyPowerValueTarget) {
      this.controller.readOnlyPowerValueTarget.textContent = node.max_power_kw ?? "—";
    }

    if (showPowerFields && !isChargePoint) {
      const existing = this.propertyChanges.get(nodeId);

      if (this.controller.hasNodeNameInputTarget) {
        this.controller.nodeNameInputTarget.value = existing?.name ?? node.name ?? "";
        this.controller.nodeNameInputTarget.disabled = isReadOnly;
      }

      if (isBattery) {
        this.populateBatteryFields(node, existing);
      } else {
        if (this.controller.hasNodeFeedInInputTarget) {
          const minKw = existing?.min_power_kw ?? node.min_power_kw;
          this.controller.nodeFeedInInputTarget.value = minKw != null ? Math.abs(minKw) : "";
          this.controller.nodeFeedInInputTarget.disabled = isReadOnly;
        }
        if (this.controller.hasNodeConsumptionInputTarget) {
          this.controller.nodeConsumptionInputTarget.value = existing?.max_power_kw ?? node.max_power_kw ?? "";
          this.controller.nodeConsumptionInputTarget.disabled = isReadOnly;
        }
      }

      if (this.controller.hasSymmetricToggleTarget) {
        if (isJunction) {
          this.controller.symmetricToggleTarget.checked = true;
          this.controller.symmetricToggleTarget.disabled = true;
        } else if (!isBattery) {
          const feedInVal = this.controller.hasNodeFeedInInputTarget ? this.controller.nodeFeedInInputTarget.value : "";
          const consVal = this.controller.hasNodeConsumptionInputTarget
            ? this.controller.nodeConsumptionInputTarget.value
            : "";
          this.controller.symmetricToggleTarget.checked = feedInVal === consVal || feedInVal === "" || consVal === "";
          this.controller.symmetricToggleTarget.disabled = isReadOnly;
        }
      }
    }

    this.controller.nodePropertiesTarget.classList.remove("hidden");
  }

  populateBatteryFields(node, existing) {
    if (this.controller.hasNodeFeedInInputTarget) {
      this.controller.nodeFeedInInputTarget.value = existing?.max_discharge_kw ?? node.max_discharge_kw ?? "";
      this.controller.nodeFeedInInputTarget.disabled = false;
    }
    if (this.controller.hasNodeConsumptionInputTarget) {
      this.controller.nodeConsumptionInputTarget.value = existing?.max_charge_kw ?? node.max_charge_kw ?? "";
      this.controller.nodeConsumptionInputTarget.disabled = false;
    }
    if (this.controller.hasBatteryCapacityInputTarget) {
      this.controller.batteryCapacityInputTarget.value = existing?.capacity_kwh ?? node.capacity_kwh ?? "";
    }
    if (this.controller.hasBatterySocMinInputTarget) {
      this.controller.batterySocMinInputTarget.value = existing?.soc_min_percent ?? node.soc_min_percent ?? 10;
    }
    if (this.controller.hasBatterySocMaxInputTarget) {
      this.controller.batterySocMaxInputTarget.value = existing?.soc_max_percent ?? node.soc_max_percent ?? 90;
    }
  }

  hideNodeProperties() {
    if (!this.controller.hasNodePropertiesTarget) return;
    this.controller.nodePropertiesTarget.classList.add("hidden");
  }

  get isSymmetric() {
    return this.controller.hasSymmetricToggleTarget && this.controller.symmetricToggleTarget.checked;
  }

  handleSymmetricToggle() {
    if (!this.isSymmetric) return;

    const feedIn = this.controller.hasNodeFeedInInputTarget ? this.controller.nodeFeedInInputTarget.value : "";
    const consumption = this.controller.hasNodeConsumptionInputTarget
      ? this.controller.nodeConsumptionInputTarget.value
      : "";
    const value = feedIn || consumption;

    if (value && this.controller.hasNodeFeedInInputTarget) this.controller.nodeFeedInInputTarget.value = value;
    if (value && this.controller.hasNodeConsumptionInputTarget)
      this.controller.nodeConsumptionInputTarget.value = value;

    this.handlePropertyChange();
  }

  handlePropertyChange() {
    const nodeId = this.selectedNodeId;
    if (!nodeId) return;

    const node = this.controller.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    if (node.type === "battery") {
      this.handleBatteryPropertyChange(node, nodeId);
      return;
    }

    if (node.type !== "junction") return;

    if (this.isSymmetric) {
      const activeEl = document.activeElement;
      if (activeEl === this.controller.nodeFeedInInputTarget && this.controller.hasNodeConsumptionInputTarget) {
        this.controller.nodeConsumptionInputTarget.value = this.controller.nodeFeedInInputTarget.value;
      } else if (activeEl === this.controller.nodeConsumptionInputTarget && this.controller.hasNodeFeedInInputTarget) {
        this.controller.nodeFeedInInputTarget.value = this.controller.nodeConsumptionInputTarget.value;
      }
    }

    const name = this.controller.hasNodeNameInputTarget ? this.controller.nodeNameInputTarget.value : node.name;
    const feedIn = this.controller.hasNodeFeedInInputTarget ? this.controller.nodeFeedInInputTarget.value : "";
    const consumption = this.controller.hasNodeConsumptionInputTarget
      ? this.controller.nodeConsumptionInputTarget.value
      : "";

    node.name = name;

    const props = { name };
    if (feedIn !== "") props.min_power_kw = -Math.abs(parseFloat(feedIn));
    if (consumption !== "") props.max_power_kw = Math.abs(parseFloat(consumption));

    this.propertyChanges.set(nodeId, props);
    this.updateSaveButtonState();

    this.controller.renderTree();
    this.attachDragBehavior();
    this.attachGhostClickHandler();
    this.highlightSelectedNode();
    this.attachDeleteButton();
  }

  handleBatteryPropertyChange(node, nodeId) {
    const name = this.controller.hasNodeNameInputTarget ? this.controller.nodeNameInputTarget.value : node.name;
    node.name = name;

    const props = { name };
    const discharge = this.controller.hasNodeFeedInInputTarget ? this.controller.nodeFeedInInputTarget.value : "";
    const charge = this.controller.hasNodeConsumptionInputTarget
      ? this.controller.nodeConsumptionInputTarget.value
      : "";
    if (discharge !== "") props.max_discharge_kw = Math.abs(parseFloat(discharge));
    if (charge !== "") props.max_charge_kw = Math.abs(parseFloat(charge));

    if (this.controller.hasBatteryCapacityInputTarget) {
      const val = this.controller.batteryCapacityInputTarget.value;
      if (val !== "") props.capacity_kwh = parseFloat(val);
    }
    if (this.controller.hasBatterySocMinInputTarget) {
      const val = this.controller.batterySocMinInputTarget.value;
      if (val !== "") props.soc_min_percent = parseInt(val, 10);
    }
    if (this.controller.hasBatterySocMaxInputTarget) {
      const val = this.controller.batterySocMaxInputTarget.value;
      if (val !== "") props.soc_max_percent = parseInt(val, 10);
    }
    this.propertyChanges.set(nodeId, props);
    this.updateSaveButtonState();

    this.controller.renderTree();
    this.attachDragBehavior();
    this.attachGhostClickHandler();
    this.highlightSelectedNode();
    this.attachDeleteButton();
  }

  handlePowerInputFocus(focused) {
    const nodeId = this.selectedNodeId;
    if (!nodeId) return;

    const node = this.controller.nodes.find((n) => n.id === nodeId);
    if (!node || !node.parent) return;

    this.controller.mainGroup.selectAll(".edge").each(function (d) {
      if (!d) return;
      const isTargetEdge =
        (d.source?.data?.id === node.parent && d.target?.data?.id === nodeId) ||
        (d.target?.data?.id === node.parent && d.source?.data?.id === nodeId);

      if (!isTargetEdge) return;

      const path = d3.select(this);
      if (focused) {
        path
          .attr("stroke-dasharray", "8,5")
          .attr("stroke", "var(--color-action-fill, #3b82f6)")
          .attr("stroke-width", 3);

        function marchingAnts() {
          path
            .transition()
            .duration(600)
            .ease(d3.easeLinear)
            .attr("stroke-dashoffset", -26)
            .on("end", function () {
              d3.select(this).attr("stroke-dashoffset", 0);
              marchingAnts();
            });
        }
        marchingAnts();
      } else {
        path.interrupt().attr("stroke-dasharray", null).attr("stroke-dashoffset", null);
      }
    });
  }

  reparentNode(nodeId, newParentId) {
    const node = this.controller.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    node.parent = newParentId;

    if (node.type === "solar_inverter") {
      const childArrays = this.controller.nodes.filter((n) => n.parent === nodeId && n.type === "solar_array");
      childArrays.forEach((child) => {
        this.pendingChanges.push({ type: "reparent", nodeId: child.id, newParentId });
      });
    } else {
      this.pendingChanges.push({ type: "reparent", nodeId, newParentId });
    }

    this.updateSaveButtonState();
  }

  addJunction(parentId) {
    const tempId = `new_junction_${Date.now()}`;
    const newNode = {
      id: tempId,
      name: this.controller.i18nAddJunctionMenuValue || "Junction",
      type: "junction",
      parent: parentId || this.findRootNodeId(),
      is_new: true,
    };

    this.controller.nodes.push(newNode);
    this.pendingChanges.push({
      type: "create",
      nodeType: "junction",
      tempId,
      parentId: newNode.parent,
      name: newNode.name,
    });

    this.updateSaveButtonState();
    this.selectNode(tempId);
    this.focusPowerInput();
  }

  addSolarArray(parentId) {
    const nodeTypes = this.controller.i18nNodeTypesValue || {};
    const resolvedParent = parentId || this.findRootNodeId();
    const inverterId = `new_inverter_${Date.now()}`;
    const arrayId = `new_solar_array_${Date.now()}`;

    const inverterNode = {
      id: inverterId,
      name: nodeTypes.solar_inverter || "Solar Inverter",
      type: "solar_inverter",
      parent: resolvedParent,
      is_new: true,
    };

    const arrayNode = {
      id: arrayId,
      name: nodeTypes.solar_array || "Solar Array",
      type: "solar_array",
      parent: inverterId,
      is_new: true,
    };

    this.controller.nodes.push(inverterNode, arrayNode);
    this.pendingChanges.push({
      type: "create",
      nodeType: "solar_array",
      tempId: arrayId,
      inverterId,
      parentId: resolvedParent,
      name: arrayNode.name,
    });

    this.updateSaveButtonState();
    this.selectNode(inverterId);
  }

  addSolarArrayToInverter(inverterId) {
    const nodeTypes = this.controller.i18nNodeTypesValue || {};
    const inverterNode = this.controller.nodes.find((n) => n.id === inverterId);
    const arrayId = `new_solar_array_${Date.now()}`;

    const arrayNode = {
      id: arrayId,
      name: nodeTypes.solar_array || "Solar Array",
      type: "solar_array",
      parent: inverterId,
      is_new: true,
    };

    this.controller.nodes.push(arrayNode);
    this.pendingChanges.push({
      type: "create",
      nodeType: "solar_array",
      tempId: arrayId,
      parentId: inverterNode.parent,
      name: arrayNode.name,
    });

    this.updateSaveButtonState();
    this.selectNode(inverterId);
  }

  addBattery(parentId) {
    const nodeTypes = this.controller.i18nNodeTypesValue || {};
    const tempId = `new_battery_${Date.now()}`;
    const newNode = {
      id: tempId,
      name: nodeTypes.battery || "Battery",
      type: "battery",
      parent: parentId || this.findRootNodeId(),
      is_new: true,
      max_charge_kw: 5,
      max_discharge_kw: 5,
      capacity_kwh: 10,
      soc_min_percent: 10,
      soc_max_percent: 90,
    };

    this.controller.nodes.push(newNode);
    this.pendingChanges.push({
      type: "create",
      nodeType: "battery",
      tempId,
      parentId: newNode.parent,
      name: newNode.name,
    });

    this.updateSaveButtonState();
    this.selectNode(tempId);
  }

  removeNode(nodeId) {
    const node = this.controller.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const isInverter = node.type === "solar_inverter";
    const hasChildren = !isInverter && this.controller.nodes.some((n) => n.parent === nodeId);
    const removable = node.type === "junction" || isInverter || node.type === "battery";
    if (hasChildren || !removable) return;

    if (isInverter) {
      const childArray = this.controller.nodes.find((n) => n.parent === nodeId && n.type === "solar_array");
      this.controller.nodes = this.controller.nodes.filter((n) => n.id !== nodeId && n.parent !== nodeId);
      if (node.is_new) {
        this.pendingChanges = this.pendingChanges.filter(
          (c) => !(c.type === "create" && (c.tempId === nodeId || c.inverterId === nodeId)),
        );
        this.propertyChanges.delete(nodeId);
        if (childArray) this.propertyChanges.delete(childArray.id);
      } else if (childArray) {
        this.pendingChanges.push({ type: "remove", nodeId: childArray.id });
      }
    } else {
      this.controller.nodes = this.controller.nodes.filter((n) => n.id !== nodeId);
      if (node.is_new) {
        this.pendingChanges = this.pendingChanges.filter((c) => !(c.type === "create" && c.tempId === nodeId));
        this.propertyChanges.delete(nodeId);
      } else {
        this.pendingChanges.push({ type: "remove", nodeId });
      }
    }

    this.controller.renderTree();
    this.attachDragBehavior();
    this.updateSaveButtonState();
  }

  findRootNodeId() {
    const root = this.controller.nodes.find((n) => !n.parent);
    return root?.id;
  }

  focusPowerInput() {
    requestAnimationFrame(() => {
      if (this.controller.hasNodeConsumptionInputTarget && !this.controller.nodeConsumptionInputTarget.disabled) {
        this.controller.nodeConsumptionInputTarget.focus();
      }
    });
  }

  findFirstIncompleteJunction() {
    return this.pendingChanges.find((c) => {
      if (c.type !== "create" || c.nodeType !== "junction") return false;
      const props = this.propertyChanges.get(c.tempId);
      return !(props?.min_power_kw < 0) && !(props?.max_power_kw > 0);
    });
  }

  trySave() {
    if (!this.hasPendingChanges) return;

    const incomplete = this.findFirstIncompleteJunction();
    if (incomplete) {
      this.selectNode(incomplete.tempId);
      this.focusPowerInput();
      return;
    }

    if (this.controller.saveModeValue === "example" || this.controller.saveModeValue === "textarea") {
      this.save();
      return;
    }

    this.showConfirmation();
  }

  updateSaveButtonState() {
    if (!this.controller.hasSaveButtonTarget) return;

    const hasChanges = this.pendingChanges.length > 0 || this.propertyChanges.size > 0;
    const hasIncomplete = !!this.findFirstIncompleteJunction();

    if (hasChanges) {
      this.controller.saveButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
      if (hasIncomplete) {
        this.controller.saveButtonTarget.classList.add("opacity-75");
      } else {
        this.controller.saveButtonTarget.classList.remove("opacity-75");
      }
    } else {
      this.controller.saveButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }
  }

  showConfirmation() {
    if (this.pendingChanges.length === 0 && this.propertyChanges.size === 0) return;

    const overlay = this.controller.confirmOverlayTarget;
    const beforeEl = overlay.querySelector("[data-role='tree-before']");
    const afterEl = overlay.querySelector("[data-role='tree-after']");

    const originalTree = this.buildTreeText(this.originalNodes);
    const currentNodes = this.controller.nodes
      .filter((n) => n.type !== "add_junction_ghost")
      .map((n) => {
        const props = this.propertyChanges.get(n.id);
        if (!props) return n;
        return {
          ...n,
          min_power_kw: props.min_power_kw ?? n.min_power_kw,
          max_power_kw: props.max_power_kw ?? n.max_power_kw,
          max_charge_kw: props.max_charge_kw ?? n.max_charge_kw,
          max_discharge_kw: props.max_discharge_kw ?? n.max_discharge_kw,
          capacity_kwh: props.capacity_kwh ?? n.capacity_kwh,
          soc_min_percent: props.soc_min_percent ?? n.soc_min_percent,
          soc_max_percent: props.soc_max_percent ?? n.soc_max_percent,
        };
      });
    const currentTree = this.buildTreeText(currentNodes);

    beforeEl.innerHTML = this.renderTreeHtml(originalTree, currentTree, "before");
    afterEl.innerHTML = this.renderTreeHtml(currentTree, originalTree, "after");

    overlay.classList.remove("hidden");
  }

  hideConfirmation() {
    this.controller.confirmOverlayTarget.classList.add("hidden");
  }

  buildTreeText(nodes) {
    const root = nodes.find((n) => !n.parent);
    if (!root) return [];

    const lines = [];
    this.buildTreeLines(root, nodes, lines, "", true);
    return lines;
  }

  buildTreeLines(node, allNodes, lines, prefix, isLast) {
    const connector = lines.length === 0 ? "" : isLast ? "\u2514\u2500 " : "\u251c\u2500 ";
    const nodeTypes = this.controller.i18nNodeTypesValue || {};
    let label = node.type === "grid_connection" ? nodeTypes[node.type] || node.name : node.name || node.type;
    if (node.type === "battery") {
      const parts = [];
      if (node.max_discharge_kw != null) parts.push(`↑${node.max_discharge_kw}`);
      if (node.max_charge_kw != null) parts.push(`↓${node.max_charge_kw}`);
      if (node.capacity_kwh != null) parts.push(`${node.capacity_kwh} kWh`);
      if (node.soc_min_percent != null || node.soc_max_percent != null)
        parts.push(`SoC ${node.soc_min_percent ?? "?"}–${node.soc_max_percent ?? "?"}%`);
      if (parts.length > 0) label += ` [${parts.join(" ")}]`;
    } else if (node.min_power_kw != null || node.max_power_kw != null) {
      const parts = [];
      if (node.min_power_kw != null) parts.push(`↑${Math.abs(node.min_power_kw)}`);
      if (node.max_power_kw != null) parts.push(`↓${node.max_power_kw}`);
      label += ` [${parts.join(" ")} kW]`;
    }
    lines.push({ id: node.id, text: prefix + connector + label, parent: node.parent, name: label });

    const children = allNodes
      .filter((n) => n.parent === node.id)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const childPrefix = lines.length === 1 ? "" : prefix + (isLast ? "   " : "\u2502  ");
    children.forEach((child, i) => {
      this.buildTreeLines(child, allNodes, lines, childPrefix, i === children.length - 1);
    });
  }

  renderTreeHtml(tree, otherTree, side) {
    const otherById = {};
    otherTree.forEach((l) => {
      otherById[l.id] = l;
    });

    const treeById = {};
    tree.forEach((l) => {
      treeById[l.id] = l;
    });

    const movedIds = new Set();
    tree.forEach((line) => {
      const other = otherById[line.id];
      if (other && other.parent !== line.parent) movedIds.add(line.id);
    });

    const hasMovedAncestor = (line) => {
      let current = line;
      while (current && current.parent) {
        if (movedIds.has(current.parent)) return true;
        current = treeById[current.parent];
      }
      return false;
    };

    return tree
      .map((line) => {
        let cls = "";
        const other = otherById[line.id];
        if (!other) {
          cls =
            side === "after"
              ? "text-green-600 dark:text-green-400 font-medium"
              : "text-red-600 dark:text-red-400 font-medium line-through";
        } else if (other.parent !== line.parent || hasMovedAncestor(line)) {
          cls = "text-blue-600 dark:text-blue-400 font-medium";
        } else if (other.name !== line.name) {
          cls = "text-amber-600 dark:text-amber-400 font-medium";
        }
        const escaped = line.text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
        return `<div class="${cls}">${escaped}</div>`;
      })
      .join("");
  }

  async save() {
    if (this.pendingChanges.length === 0 && this.propertyChanges.size === 0) return;

    const validationError = this.validateBatteryBounds();
    if (validationError) {
      this.showSaveError(validationError);
      return;
    }

    if (this.controller.saveModeValue === "example") {
      await this.saveToExample();
      return;
    }

    if (this.controller.saveModeValue === "textarea") {
      this.saveToTextarea();
      return;
    }

    if (!this.controller.hasTopologyNodesUrlValue) {
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const headers = {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    };

    const idMapping = {};

    try {
      for (const change of this.pendingChanges) {
        const resolvedParentId = idMapping[change.newParentId] || change.newParentId;
        const resolvedNodeId = idMapping[change.nodeId] || change.nodeId;

        if (change.type === "create") {
          const resolvedParent = idMapping[change.parentId] || change.parentId;
          const parentTnId = this.extractTopologyNodeId(resolvedParent);
          const props = this.propertyChanges.get(change.tempId) || {};
          const body = {
            name: props.name || change.name,
            parent_id: parentTnId,
            node_type: change.nodeType || "junction",
          };
          if (props.min_power_kw !== undefined) body.min_power_kw = props.min_power_kw;
          if (props.max_power_kw !== undefined) body.max_power_kw = props.max_power_kw;
          if (change.nodeType === "battery") {
            this.addBatteryParams(body, props);
          }

          const response = await fetch(this.controller.topologyNodesUrlValue, {
            method: "POST",
            headers,
            body: JSON.stringify(body),
          });

          if (!response.ok) throw new Error(`Failed to create node: ${response.status}`);
          const data = await response.json();
          idMapping[change.tempId] = `tn_${data.id}`;
          if (change.inverterId) {
            idMapping[change.inverterId] = `inv_tn_${data.id}`;
          }
          this.propertyChanges.delete(change.tempId);
        } else if (change.type === "reparent") {
          const tnId = this.extractTopologyNodeId(resolvedNodeId);
          const parentTnId = this.extractTopologyNodeId(resolvedParentId);

          const response = await fetch(`${this.controller.topologyNodesUrlValue}/${tnId}`, {
            method: "PATCH",
            headers,
            body: JSON.stringify({ parent_id: parentTnId }),
          });

          if (!response.ok) throw new Error(`Failed to reparent: ${response.status}`);
        } else if (change.type === "remove") {
          const tnId = this.extractTopologyNodeId(resolvedNodeId);

          const response = await fetch(`${this.controller.topologyNodesUrlValue}/${tnId}`, {
            method: "DELETE",
            headers,
          });

          if (!response.ok) throw new Error(`Failed to remove: ${response.status}`);
        }
      }

      for (const [nodeId, props] of this.propertyChanges) {
        const resolvedId = idMapping[nodeId] || nodeId;
        const tnId = this.extractTopologyNodeId(resolvedId);
        const node = this.controller.nodes.find((n) => n.id === nodeId);

        const body = {};
        if (props.name !== undefined) body.name = props.name;
        if (node?.type === "battery") {
          this.addBatteryParams(body, props);
        } else {
          if (props.min_power_kw !== undefined) body.min_power_kw = props.min_power_kw;
          if (props.max_power_kw !== undefined) body.max_power_kw = props.max_power_kw;
        }

        const response = await fetch(`${this.controller.topologyNodesUrlValue}/${tnId}`, {
          method: "PATCH",
          headers,
          body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error(`Failed to update properties: ${response.status}`);
      }

      this.removeNavigationProtection();
      window.location.reload();
    } catch (error) {
      console.error("Failed to save topology changes:", error);
      this.showSaveError();
    }
  }

  addBatteryParams(body, props) {
    if (props.max_charge_kw !== undefined) body.max_charge_kw = props.max_charge_kw;
    if (props.max_discharge_kw !== undefined) body.max_discharge_kw = props.max_discharge_kw;
    if (props.capacity_kwh !== undefined) body.capacity_kwh = props.capacity_kwh;
    if (props.soc_min_percent !== undefined) body.soc_min_percent = props.soc_min_percent;
    if (props.soc_max_percent !== undefined) body.soc_max_percent = props.soc_max_percent;
  }

  extractTopologyNodeId(id) {
    if (typeof id === "string" && id.startsWith("tn_")) {
      return id.replace("tn_", "");
    }
    return id;
  }

  validateBatteryBounds() {
    for (const node of this.controller.nodes) {
      if (node.type !== "battery") continue;
      const props = this.propertyChanges.get(node.id) || {};
      const socMin = props.soc_min_percent ?? node.soc_min_percent;
      const socMax = props.soc_max_percent ?? node.soc_max_percent;
      if (socMin != null && socMax != null && parseFloat(socMin) >= parseFloat(socMax)) {
        const template =
          this.controller.i18nSocBoundsErrorValue || "SoC minimum (%{min}%) must be lower than maximum (%{max}%)";
        return `${node.name}: ${template.replace("%{min}", socMin).replace("%{max}", socMax)}`;
      }
    }
    return null;
  }

  showSaveError(message) {
    if (!this.controller.hasEditToolbarTarget) return;

    const errorEl = this.controller.editToolbarTarget.querySelector("[data-role='save-error']");
    if (errorEl) {
      if (message) errorEl.textContent = message;
      errorEl.classList.remove("hidden");
      setTimeout(() => errorEl.classList.add("hidden"), 5000);
    }
  }

  async saveToExample() {
    const nodes = this.controller.nodes;
    const treeData = this.nodesToTreeData(nodes);
    const saveUrl = this.controller.saveUrlValue;

    if (!saveUrl) {
      console.error("No save URL configured");
      this.showSaveError();
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(saveUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({ digital_twin_example: { tree_data: treeData } }),
      });

      if (!response.ok) throw new Error(`Failed to save: ${response.status}`);

      this.removeNavigationProtection();
      window.location.reload();
    } catch (error) {
      console.error("Failed to save topology:", error);
      this.showSaveError();
    }
  }

  saveToTextarea() {
    const nodes = this.controller.nodes;
    const treeData = this.nodesToTreeData(nodes);

    this.controller.element.dispatchEvent(
      new CustomEvent("topology-editor:saved", {
        bubbles: true,
        detail: { treeData },
      }),
    );

    this.removeNavigationProtection();
    this.exit();
    this.controller.renderTree();
    this.controller.fitToView();
  }

  nodesToTreeData(nodes) {
    for (const [nodeId, changes] of this.propertyChanges) {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) Object.assign(node, changes);
    }

    const gridNode = nodes.find((n) => n.type === "grid_connection");
    if (!gridNode) return {};

    const childrenOf = new Map();
    for (const node of nodes) {
      if (node.parent) {
        if (!childrenOf.has(node.parent)) childrenOf.set(node.parent, []);
        childrenOf.get(node.parent).push(node);
      }
    }

    const grid = {
      name: gridNode.name,
      power_range_kw: [gridNode.min_power_kw ?? 0, gridNode.max_power_kw ?? 0],
      address: gridNode.address || undefined,
    };

    const children = (childrenOf.get(gridNode.id) || []).map((child) => this.nodeToTreeChild(child, childrenOf));

    return { grid, children };
  }

  nodeToTreeChild(node, childrenOf) {
    const result = { type: node.type, name: node.name };

    if (node.type === "junction") {
      result.power_range_kw = [node.min_power_kw ?? 0, node.max_power_kw ?? 0];
    } else if (node.type === "battery") {
      result.max_charge_kw = node.max_charge_kw ?? node.max_power_kw ?? 0;
      result.max_discharge_kw = node.max_discharge_kw ?? Math.abs(node.min_power_kw ?? 0);
      result.capacity_kwh = node.capacity_kwh ?? 0;
      result.soc_min_percent = node.soc_min_percent ?? 10;
      result.soc_max_percent = node.soc_max_percent ?? 90;
      result.initial_soc_percent = node.initial_soc_percent ?? 50;
    } else if (node.type === "solar_array") {
      result.panel_count = node.panel_count ?? 0;
      result.wp_per_panel = node.wp_per_panel ?? 0;
      result.declination = node.declination ?? 0;
      result.azimuth = node.azimuth ?? 0;
    } else if (node.type === "charge_point" || node.type === "solar_inverter") {
      result.power_range_kw = [node.min_power_kw ?? 0, node.max_power_kw ?? 0];
      result.metadata = { ...(node.metadata || {}), ...(node.model ? { model: node.model } : {}) };
    } else if (node.type === "connector") {
      result.power_range_kw = [node.min_power_kw ?? 0, node.max_power_kw ?? 0];
      result.metadata = {
        ...(node.metadata || {}),
        ...(node.identifier ? { identifier: node.identifier } : {}),
        ...(node.number_of_phases ? { phases: node.number_of_phases } : {}),
      };
    } else if (node.type === "other_loads") {
      result.power_range_kw = [node.min_power_kw ?? -1000, node.max_power_kw ?? 1000];
    }

    const children = childrenOf.get(node.id) || [];
    if (children.length > 0) {
      result.children = children.map((child) => this.nodeToTreeChild(child, childrenOf));
    }

    return result;
  }

  get hasPendingChanges() {
    return this.pendingChanges.length > 0 || this.propertyChanges.size > 0;
  }
}
